import { useCallback, useEffect, useRef, useState } from 'react'
import { useGameStore, Move, BotBattleConfig } from '../stores/gameStore'
import { selectNeuralPolicyMove, selectNeuralMctsMove, warmupNeuralPolicy } from '../ai/neuralPolicy'
import { selectSimpleAIMove } from '../ai/simpleAi'
import { createNewGame, applyMove } from '../game/engine'
import { getMoveDurationMs } from '../utils/animation'

// Difficulty ladder: easy = centered greedy, medium = centered 2-ply minimax
// (client-side heuristics), hard = neural policy-greedy, advanced = neural
// policy+value MCTS (candidate_v2 iter400 run directly via ONNX). The game state
// is driven entirely in the browser (createNewGame/applyMove) — no backend.

// Pause between bot moves (ms) so the game is watchable.
const MOVE_DELAY_MS = 500

export function useBotBattle() {
  const store = useGameStore()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const aiInFlight = useRef(false)
  const moveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  // Map the current player-colour to the bot controlling it. The engine always
  // moves player 1 (Red) first, so "who goes first" decides which bot is player 1.
  const getCurrentDifficulty = useCallback((currentPlayer: number): BotBattleConfig['bot1Difficulty'] => {
    const bot1Player = store.botBattle.bot1First ? 1 : 2
    return currentPlayer === bot1Player
      ? store.botBattle.bot1Difficulty
      : store.botBattle.bot2Difficulty
  }, [store.botBattle.bot1Difficulty, store.botBattle.bot2Difficulty, store.botBattle.bot1First])

  const startNewGame = useCallback(() => {
    setError(null)
    aiInFlight.current = false
    setIsPaused(false)

    // Preload the neural model if either bot needs it.
    const { bot1Difficulty, bot2Difficulty } = store.botBattle
    if ([bot1Difficulty, bot2Difficulty].some((d) => d === 'hard' || d === 'advanced')) {
      warmupNeuralPolicy()
    }

    const gameState = createNewGame()
    store.setGameState({
      ...gameState,
      selectedPiece: null,
      validMoves: [],
      animatingPiece: null,
      isAIThinking: false,
      botBattle: { ...store.botBattle, enabled: true },
    })
  }, [store])

  const makeMove = useCallback(
    async (move: Move) => {
      const s = useGameStore.getState()
      if (!s.gameId) return

      setIsLoading(true)
      setError(null)
      store.setGameState({
        isAIThinking: true,
        selectedPiece: null,
        validMoves: [],
      })

      try {
        const gameState = applyMove(
          {
            gameId: s.gameId,
            pieces: s.pieces,
            currentPlayer: s.currentPlayer,
            winner: s.winner,
            legalMoves: s.legalMoves,
            moveHistory: s.moveHistory,
          },
          move,
        )
        const animationMs = getMoveDurationMs(move.path)

        store.setAnimating({
          from: move.start,
          to: move.end,
          path: move.path,
        })

        await new Promise((resolve) => setTimeout(resolve, animationMs))

        store.setAnimating(null)
        store.setGameState({
          ...gameState,
          selectedPiece: null,
          validMoves: [],
          isAIThinking: false,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        store.setGameState({ isAIThinking: false, animatingPiece: null })
      } finally {
        setIsLoading(false)
      }
    },
    [store],
  )

  const runAIMove = useCallback(async () => {
    if (aiInFlight.current) return
    if (!store.gameId || store.winner !== null || !store.legalMoves.length) return
    if (store.isAIThinking || store.animatingPiece) return

    aiInFlight.current = true
    store.setGameState({ isAIThinking: true })

    try {
      const currentDifficulty = getCurrentDifficulty(store.currentPlayer)
      let move: Move | null = null
      const neuralArgs = {
        pieces: store.pieces,
        currentPlayer: store.currentPlayer,
        legalMoves: store.legalMoves,
      }

      if (currentDifficulty === 'advanced') {
        move = (await selectNeuralMctsMove(neuralArgs)).move
      } else if (currentDifficulty === 'hard') {
        move = (await selectNeuralPolicyMove(neuralArgs)).move
      } else {
        move = (await selectSimpleAIMove({ ...neuralArgs, difficulty: currentDifficulty })).move
      }

      if (move) {
        await makeMove(move)
      } else {
        store.setGameState({ isAIThinking: false })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      store.setGameState({ isAIThinking: false, animatingPiece: null })
    } finally {
      aiInFlight.current = false
    }
  }, [
    makeMove,
    store.currentPlayer,
    store.gameId,
    store.animatingPiece,
    store.legalMoves,
    store.moveHistory.length,
    store.pieces,
    store.winner,
    getCurrentDifficulty,
  ])

  // Main game loop
  useEffect(() => {
    if (isPaused || store.winner !== null || isLoading) return

    // Clear any existing timeout
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current)
    }

    // Schedule the next AI move after a short viewing delay.
    moveTimeoutRef.current = setTimeout(() => {
      void runAIMove()
    }, MOVE_DELAY_MS)

    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current)
      }
    }
  }, [store.currentPlayer, isPaused, store.winner, isLoading, runAIMove])

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev)
  }, [])

  return {
    ...store,
    startNewGame,
    makeMove,
    togglePause,
    isPaused,
    error,
    isLoading,
  }
}
